import { create } from 'zustand';
import { nextAppointment } from '../utils/appointment';
import { persist } from 'zustand/middleware';
import { calculateLevelBasedPoints, POINTS } from '../utils/levelCalculation';
import { formatDateToLocal, parseLocalDate } from '../utils/date';
import type { Event } from '../types';
import { supabase } from '../lib/supabase';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import { cloudVersionsOf, createOwnRowSync, marksOf, mergeLoad, ownCache, rowFocusRefresh, type OwnRowSync, type RowMarks } from '../lib/ownRowSync';
import { refreshBlobOnFocus } from '../lib/blobFocusRefresh';
import { accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount } from '../lib/accountStorage';
import { useActivityLogStore } from './activityLogStore';

// ─── Supabase sync helpers (sync epic — same pattern as task/habit stores) ──
// The `events` table keeps typed columns (date-range/category queries) PLUS a
// `data` jsonb holding the full Event (e.g. `discipline` has no column). Writes
// are fire-and-forget: optimistic local first, cloud after; failures only log.
// ⚠️⚠️ A DELETED EVENT STAYS DELETED (2026-10-18): writes go through `lib/ownRowSync.ts` —
// only events created here and unconfirmed (`unsyncedIds`) are inserted, every other write
// is an UPDATE, and an event the cloud no longer has is dropped here, never re-created.
// ⚠️⚠️ AN EDIT THAT NEVER LANDED IS KEPT AND SAVED (2026-10-20). A load took the cloud's copy
// of every event, so an edit whose write never landed (offline, or a sign-out on its way) was
// lost on the next load. Now an unsaved edit of the event as the cloud still has it is kept and
// saved (`writeBack`) — decided from what this device recorded, never a clock (lib/ownRowSync.ts);
// one of an event another device has written since is merged onto that device's copy, from the
// base saved with it (`dirtyBases`, owner 2026-10-20).
// Every edit still stamps `updatedAt`, and a load reads it from the row's `updated_at` (each
// write sets it — no trigger on `events`): when the event was last written. No load compares it.

const EVENTS_TABLE = 'events';

/** The row's columns, without its id (an UPDATE never sets the id). */
function eventFields(event: Event, userId: string) {
  return {
    user_id: userId,
    title: event.title,
    description: event.description ?? null,
    date: event.date,
    start_time: event.startTime,
    end_time: event.endTime,
    location: event.location ?? null,
    category: event.category ?? '',
    completed: event.completed ?? false,
    point_record: event.pointRecord ?? null,
    // created_at omitted → DB default on insert, never touched by an update.
    updated_at: new Date().toISOString(),
    data: event,
  };
}

function rowToEvent(row: any): Event {
  const data = row?.data;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) {
    // When the row was last WRITTEN (never null — see the header).
    return { ...data, updatedAt: row.updated_at } as Event;
  }
  return {
    id: row.id,
    title: row.title,
    description: row.description ?? undefined,
    date: row.date,
    startTime: row.start_time,
    endTime: row.end_time,
    location: row.location ?? undefined,
    category: row.category ?? '',
    completed: row.completed ?? false,
    pointRecord: row.point_record ?? undefined,
  } as Event;
}

const eventSync: OwnRowSync<Event> = createOwnRowSync<Event>({
  table: EVENTS_TABLE,
  label: 'events',
  fields: eventFields,
  current: (id) => useEventStore.getState().events.find((e) => e.id === id),
  fromRow: rowToEvent,
  replace: (event) => useEventStore.setState((s) => ({ events: s.events.map((e) => (e.id === event.id ? event : e)) })),
  unsyncedIds: () => useEventStore.getState().unsyncedIds,
  confirm: (id) => useEventStore.setState((s) =>
    s.unsyncedIds.includes(id) ? { unsyncedIds: s.unsyncedIds.filter((x) => x !== id) } : s),
  drop: (id) => useEventStore.setState((s) => ({
    events: s.events.filter((e) => e.id !== id),
    unsyncedIds: s.unsyncedIds.filter((x) => x !== id),
  })),
  marks: () => useEventStore.getState(),
  setMarks: (marks) => useEventStore.setState(marks),
  name: (e) => e.title,
});

/** Save these events (each through its own queue — see `lib/ownRowSync.ts`). */
const cloudSave = (events: Event | Event[]): Promise<boolean> => eventSync.save(events);

async function cloudDelete(ids: string | string[]): Promise<void> {
  const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean);
  if (list.length === 0) return;
  // ⚠️ After any save of these events already on the wire: a delete that overtook the
  // event's own insert would be undone by it.
  await eventSync.settle(list);
  try {
    const { user } = await syncManager.safeAuthCheck();
    if (!user) return;
    const { error } = await supabase.from(EVENTS_TABLE).delete().in('id', list);
    if (error) console.error('Failed to delete event(s) from Supabase:', error);
  } catch (error) {
    console.error('Error deleting event(s) from Supabase:', error);
  }
}

type EventCompletionCallback = (event: Event) => void;

interface EventState extends RowMarks<Event> {
  events: Event[];
  userLevel: number;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /** Events created on THIS device whose insert the cloud hasn't confirmed (see lib/ownRowSync.ts). Persisted. */
  unsyncedIds: string[];
  // `dirtyIds` + `cloudVersions` + `dirtyBases` (RowMarks, persisted): what a load decides by, without a clock — lib/ownRowSync.ts.
  initialize: () => Promise<void>;
  addEvent: (event: Omit<Event, 'id' | 'completed'>) => void;
  updateEvent: (id: string, updates: Partial<Event>) => void;
  deleteEvent: (id: string) => void;
  toggleEvent: (id: string, onComplete?: EventCompletionCallback) => void;
  getEventsByDate: (date: string) => Event[];
  getEventsByDateRange: (startDate: string, endDate: string) => Event[];
  setUserLevel: (level: number) => void;
}

export const useEventStore = create<EventState>()(
  persist<EventState, [], [], any>(
    (set, get) => ({
      events: [],
      userLevel: 1,
      initializedUserId: null,
      cachedUserId: null,
      unsyncedIds: [],
      dirtyIds: [],
      cloudVersions: {},
      dirtyBases: {},

      // Load-on-login, keyed by user id (re-runs on account switch). The cloud's
      // copy wins except an unsaved edit of the event (merged onto it if written since); a
      // cached event the cloud didn't return was deleted elsewhere and goes, unless
      // it is unsynced (lib/ownRowSync.ts mergeLoad). What the merge kept over the
      // cloud is saved.
      initialize: () => dedupeInit('events', async () => {
        const at = accountGeneration();
        try {
          const { user } = await syncManager.safeAuthCheck();
          if (!user) return;
          if (get().initializedUserId === user.id) return;
          // Cache-first boot: while this user's local cache is fresh, skip the
          // cloud read entirely (write-through keeps cloud current meanwhile).
          if (get().cachedUserId === user.id && isCloudFresh('events', user.id)) {
            set({ initializedUserId: user.id });
            return;
          }

          const { result: { data, error }, win } = await eventSync.load(() => supabase
            .from(EVENTS_TABLE)
            .select('*'));
          // An answer for an account that has since signed out is not this copy's.
          if (at !== accountGeneration()) return;
          if (error) throw error;

          const cloudEvents = (data ?? []).map(rowToEvent);

          let kept: string[] = [];
          set((state) => {
            const merged = mergeLoad({
              ...ownCache(state.cachedUserId, user.id, state.events, state.unsyncedIds),
              marks: marksOf(state),
              cloud: cloudEvents,
              versions: cloudVersionsOf(data ?? []),
              win,
              label: 'events',
            });
            kept = merged.kept;
            return {
              events: merged.rows,
              unsyncedIds: merged.unsyncedIds,
              ...merged.marks,
              initializedUserId: user.id,
              cachedUserId: user.id,
            };
          });
          eventSync.seen(cloudEvents, win); // the base of each event's next edit (lib/ownRowSync.ts)
          void eventSync.writeBack(kept); // what the merge kept over the cloud (unsynced, or an unsaved edit)
          markCloudFresh('events', user.id);
        } catch (error) {
          console.error('Failed to initialize events from Supabase:', error);
        }
      }),

      setUserLevel: (level) => set({ userLevel: level }),

      addEvent: (eventData) => {
        // Ensure date is in local timezone YYYY-MM-DD format
        const localDate = formatDateToLocal(new Date(eventData.date));

        const event: Event = {
          ...eventData,
          id: crypto.randomUUID(),
          completed: false,
          date: localDate
        };

        set(state => ({
          events: [...state.events, event],
          unsyncedIds: [...state.unsyncedIds, event.id], // ← INSERTED until the cloud confirms it
        }));

        void cloudSave(event);
      },

      updateEvent: (id, updates) => {
        set(state => ({
          events: state.events.map(event => {
            if (event.id !== id) return event;
            
            const updatedEvent = { ...event, ...updates, updatedAt: new Date().toISOString() };
            if (updates.date) {
              // Convert date to local timezone YYYY-MM-DD format
              updatedEvent.date = formatDateToLocal(new Date(updates.date));
            }
            
            return updatedEvent;
          })
        }));

        const updated = get().events.find(e => e.id === id);
        if (updated) void cloudSave(updated);
      },

      deleteEvent: (id) => {
        eventSync.noteDeleted(id);
        set(state => ({
          events: state.events.filter(event => event.id !== id),
          unsyncedIds: state.unsyncedIds.filter((x) => x !== id),
        }));

        void cloudDelete(id);
      },

      toggleEvent: (id, onComplete) => {
        /**
         * ⚠️ Captured BEFORE the toggle. Un-completing an event KEEPS its point
         * record, so a re-completion mints nothing — only the first completion
         * is real XP, and only that one belongs in the journal. Logging every
         * completion would inflate the Progress Log's XP chart with points the
         * user was never actually awarded.
         */
        const hadPointRecord = !!get().events.find(e => e.id === id)?.pointRecord;
        set(state => {
          const event = state.events.find(e => e.id === id);
          if (!event) return state;

          const completed = !event.completed;
          
          // Only create a new point record if the event is being completed and doesn't already have one
          let pointRecord = event.pointRecord;
          if (completed && !pointRecord) {
            const [startHour, startMinute] = event.startTime.split(':').map(Number);
            const [endHour, endMinute] = event.endTime.split(':').map(Number);
            const duration = (endHour * 60 + endMinute) - (startHour * 60 + startMinute);
            const durationHours = Math.max(1, Math.ceil(duration / 60));

            const basePoints = calculateLevelBasedPoints(POINTS.EVENT, state.userLevel);
            const pointsPerHour = Math.round(basePoints * 0.5);
            const durationPoints = Math.round(pointsPerHour * (durationHours - 1));
            const totalPoints = basePoints + Math.max(0, durationPoints);

            pointRecord = {
              points: totalPoints,
              userLevel: state.userLevel,
              earnedAt: new Date().toISOString()
            };
          }

          const updatedEvent = {
            ...event,
            completed,
            pointRecord: completed ? pointRecord : event.pointRecord,
            updatedAt: new Date().toISOString(),
          };

          // Call completion callback if provided and event was completed
          if (completed && onComplete) {
            onComplete(updatedEvent);
          }

          return {
            events: state.events.map(e =>
              e.id === id ? updatedEvent : e
            )
          };
        });

        const toggled = get().events.find(e => e.id === id);
        if (toggled) void cloudSave(toggled);

        // ⭐ A REPEATING HEALTH APPOINTMENT BOOKS ITS NEXT ONE when completed
        // (owner, 2026-10-05). `appointment.nextId` remembers the booking, so
        // un-completing and completing again never books a second one.
        const next = toggled?.completed ? nextAppointment(toggled) : null;
        if (toggled && next) {
          const booked: Event = { ...next, id: crypto.randomUUID() };
          const withLink: Event = { ...toggled, appointment: { ...toggled.appointment!, nextId: booked.id } };
          set(state => ({
            events: [...state.events.map(e => (e.id === toggled.id ? withLink : e)), booked],
            unsyncedIds: [...state.unsyncedIds, booked.id], // a new event: INSERTED
          }));
          void cloudSave([withLink, booked]);
        }

        // The journal entry. `activityType: 'event'` had a render branch and a
        // filter option but no writer, so "Events" in Reflection → Activity was
        // empty forever regardless of how many events were completed.
        if (toggled?.completed && toggled.pointRecord && !hadPointRecord) {
          void useActivityLogStore.getState().addActivity({
            activityType: 'event',
            activityName: toggled.title,
            domainId: toggled.category || '',
            xpEarned: toggled.pointRecord.points,
            metadata: { eventId: toggled.id },
            completedAt: new Date().toISOString(),
          });
        }
      },

      getEventsByDate: (date) => {
        // Convert input date to local timezone YYYY-MM-DD format
        const localDate = formatDateToLocal(new Date(date));
        return get().events.filter(event => event.date === localDate);
      },

      getEventsByDateRange: (startDate, endDate) => {
        // Convert input dates to local timezone YYYY-MM-DD format
        const localStartDate = formatDateToLocal(new Date(startDate));
        const localEndDate = formatDateToLocal(new Date(endDate));
        
        return get().events.filter(event => {
          const eventDate = event.date;
          return eventDate >= localStartDate && eventDate <= localEndDate;
        });
      }
    }),
    {
      name: 'events-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 1,
      // Persist the cache + owner; `initializedUserId` stays transient.
      partialize: (state) => ({
        events: state.events,
        userLevel: state.userLevel,
        cachedUserId: state.cachedUserId,
        // ⚠️ A cache from before this field has none: all cloud copies (ghosts go on load).
        unsyncedIds: state.unsyncedIds,
        // ⚠️ A cache from before these has no marks: no unsaved edit, so its first load takes the cloud's copies.
        dirtyIds: state.dirtyIds,
        cloudVersions: state.cloudVersions,
        // ⚠️ Each unsaved row's base: without it a load drops that edit (lib/ownRowSync.ts). A cache from
        // before it has none.
        dirtyBases: state.dirtyBases,
      }),
      // The saved copy read back (a page load, a change of account): the base of each event's next
      // edit — as this device last saw it in the cloud; an unsaved one's, its saved base (lib/ownRowSync.ts
      // `restore`). ⚠️ Runs inside `create`, before `useEventStore` exists: the marks come with `state`.
      onRehydrateStorage: () => (state) => { if (state) eventSync.restore(state.events, state); },
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          return {
            events: [],
            userLevel: 1
          } as unknown as EventState;
        }
        return persistedState as EventState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: the next account saw the last
   one's calendar until its own load; events that lived only here were replaced by the next account's
   load; and the next account's events made before its own load were kept by the last one's. */
scopeToAccount(useEventStore);

// ⭐ A tab that comes back shows the events the cloud holds now — read only (lib/ownRowSync.ts `rowFocusRefresh`).
refreshBlobOnFocus(EVENTS_TABLE, rowFocusRefresh(eventSync, {
  label: 'events',
  loadedFor: () => useEventStore.getState().initializedUserId,
  held: () => useEventStore.getState().events,
  versions: () => supabase.from(EVENTS_TABLE).select('id, updated_at'),
  apply: (read) => useEventStore.setState((state) => {
    const merged = mergeLoad({ cached: state.events, unsyncedIds: state.unsyncedIds, marks: marksOf(state), ...read.over(state.events), label: 'events' });
    return { events: merged.rows, unsyncedIds: merged.unsyncedIds, ...merged.marks };
  }),
}));
